import { api, type AuthRequestConfig } from '@/lib/api/axios';
import type { User } from '@/types/domain/user';
import type {
  AuthLoginApiResponseDto,
  AuthLoginResponseDto,
  BookingManagementTokenRequestDto,
  SignInUserDto,
} from '@/types/dto/auth.dto';
import type { RequestCodeDto, ValidateCodeDto, ValidateCodeResponseDto } from '@/types/dto/phone-auth.dto';
import type { AuthProfileResponseDto, AuthProfileUserDto, UpdateProfileRequestDto } from '@/types/dto/profile.dto';
import { isAxiosError } from 'axios';

function normalizeText(value?: string | null) {
  const normalizedValue = value?.trim();

  return normalizedValue ? normalizedValue : null;
}

function normalizeBirthDate(value?: string | null) {
  return normalizeText(value) ?? undefined;
}

function normalizeProfileUser(user?: AuthProfileUserDto | null): User | null {
  if (!user) {
    return null;
  }

  return {
    ...user,
    birthDate: normalizeBirthDate(user.birthDate),
    email: normalizeText(user.email),
    name: normalizeText(user.name ?? user.fullName),
    phoneNumber: normalizeText(user.phoneNumber),
    photo: normalizeText(user.photo),
    socialNumber: normalizeText(user.socialNumber),
  };
}

function isProfileUserPayload(value: unknown): value is AuthProfileUserDto {
  if (!value || typeof value !== 'object') {
    return false;
  }

  return (
    'id' in value ||
    'email' in value ||
    'fullName' in value ||
    'name' in value ||
    'phoneNumber' in value ||
    'photo' in value ||
    'socialNumber' in value
  );
}

function readProfileResponse(data: AuthProfileResponseDto | string | undefined): User | null {
  if (!data || typeof data === 'string') {
    return null;
  }

  if (typeof data === 'object' && 'user' in data) {
    return isProfileUserPayload(data.user) ? normalizeProfileUser(data.user) : null;
  }

  return isProfileUserPayload(data) ? normalizeProfileUser(data) : null;
}

export const AuthService = {
  signIn: async (payload: SignInUserDto): Promise<AuthLoginResponseDto> => {
    const { data } = await api.post<AuthLoginApiResponseDto>('/auth/signIn', payload, {
      _skipAuthRefresh: true,
    } as AuthRequestConfig);

    return {
      ...data,
      user: normalizeProfileUser(data.user) ?? data.user,
    };
  },
  signInWithBookingManagementToken: async (
    payload: BookingManagementTokenRequestDto,
  ): Promise<AuthLoginResponseDto> => {
    const { data } = await api.post<AuthLoginApiResponseDto>('/auth/booking-management-token', payload, {
      _skipAuthRedirect: true,
      _skipAuthRefresh: true,
    } as AuthRequestConfig);

    return {
      ...data,
      user: normalizeProfileUser(data.user) ?? data.user,
    };
  },
  requestCode: async (payload: RequestCodeDto): Promise<void> => {
    await api.post('/auth/request-code', payload, { _skipAuthRefresh: true } as AuthRequestConfig);
  },
  validateCode: async (payload: ValidateCodeDto): Promise<ValidateCodeResponseDto> => {
    const { data } = await api.post<ValidateCodeResponseDto>('/auth/validate-code', payload, {
      _skipAuthRefresh: true,
    } as AuthRequestConfig);

    return data;
  },
  profile: async (): Promise<User | null> => {
    try {
      const { data } = await api.get<AuthProfileResponseDto>('/auth/profile', {
        _skipAuthRedirect: true,
        headers: {
          'Cache-Control': 'no-store',
          Expires: '0',
          'If-None-Match': '',
          'If-Modified-Since': '0',
          Pragma: 'no-cache',
        },
        params: { _t: Date.now() },
      } as AuthRequestConfig);

      return readProfileResponse(data as AuthProfileResponseDto);
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 401) {
        return null;
      }

      throw error;
    }
  },
  updateProfile: async (payload: UpdateProfileRequestDto): Promise<User | null> => {
    const { data } = await api.patch<AuthProfileResponseDto | string>('/auth/profile', payload);

    return readProfileResponse(data as AuthProfileResponseDto | string);
  },
  uploadProfilePhoto: async (file: File): Promise<User | null> => {
    const formData = new FormData();

    formData.append('file', file);

    const { data } = await api.post<AuthProfileResponseDto | string>('/auth/profile/photo', formData);

    return readProfileResponse(data as AuthProfileResponseDto | string);
  },
  logout: async (): Promise<void> => {
    await api.post('/auth/logout');
  },
};
